import { create } from "zustand";
import { toast } from "react-toastify";
import {
  getCurrentDateTime,
  getStartOfCurrentDate,
} from "../../../Common/helper/helpar";
import { getRFIDAssetReport } from "../../../Apis/rfidApi";

const getDefaultReportDates = () => {
  const from = new Date();
  from.setDate(from.getDate() - 7);
  const year = from.getFullYear();
  const month = String(from.getMonth() + 1).padStart(2, "0");
  const day = String(from.getDate()).padStart(2, "0");
  return {
    fromDate: `${year}-${month}-${day}T00:00`,
    toDate: getCurrentDateTime() || getStartOfCurrentDate(),
  };
};

const formatScannedOn = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getErrorMessage = (data, fallback) => {
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  return fallback;
};

const normalizeCoordinate = (value) =>
  value != null && value !== "" ? String(value).replace(",", ".") : "";

export const mapHandHeldReportData = (list = []) =>
  list.map((item) => ({
    ...item,
    assetCode: item.assetCode ?? item.AssetCode ?? "N/A",
    assetCategoryName:
      item.assetCategory ??
      item.assetCategoryName ??
      item.AssetCategory ??
      "N/A",
    assetTypeName: item.assetType ?? item.assetTypeName ?? item.AssetType ?? "N/A",
    imeiSlMfgNo: item.imeiNo ?? item.imeiSlMfgNo ?? item.ImeiNo ?? "N/A",
    wardNo: item.circleName ?? item.wardNo ?? item.WardNo ?? "N/A",
    zoneNo: item.zoneName ?? item.zoneNo ?? item.ZoneName ?? "N/A",
    scannedByReader:
      item.scannedByReader ?? item.ScannedByReader ?? "N/A",
    scannedOn: formatScannedOn(item.scannedOn ?? item.ScannedOn),
    latitude: normalizeCoordinate(item.latitude ?? item.Latitude),
    longitude: normalizeCoordinate(item.longitude ?? item.Longitude),
    rfidCode: item.assetCode ?? item.rfidCode ?? "",
    rfidCategoryName:
      item.assetCategory ?? item.assetCategoryName ?? item.rfidCategoryName ?? "",
    rfidDeviceName: item.scannedByReader ?? item.rfidDeviceName ?? "",
  }));

export const getDefaultHandHeldFilters = () => ({
  circleId: "",
  assetCategoryId: "",
  assetTypeId: "",
  ...getDefaultReportDates(),
});

export const DEFAULT_HANDHELD_FILTERS = getDefaultHandHeldFilters();

let handHeldFetchSeq = 0;

export const useHandHeldReaderCollectionStore = create((set, get) => ({
  reportRows: [],
  isLoading: false,
  error: "",
  filters: getDefaultHandHeldFilters(),
  pagination: {
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 1,
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  resetFilters: () => {
    const defaults = getDefaultHandHeldFilters();
    set({
      filters: defaults,
      error: "",
      pagination: { ...get().pagination, pageNumber: 1 },
    });
    return get().fetchHandHeldReaderCollection({
      ...defaults,
      pageNumber: 1,
    });
  },

  setReportPage: (pageNumber) => {
    set((state) => ({
      pagination: { ...state.pagination, pageNumber },
    }));
    return get().fetchHandHeldReaderCollection({ pageNumber });
  },

  setReportPageSize: (pageSize) => {
    set((state) => ({
      pagination: { ...state.pagination, pageSize, pageNumber: 1 },
    }));
    return get().fetchHandHeldReaderCollection({ pageNumber: 1, pageSize });
  },

  fetchHandHeldReaderCollection: async (queryParams = {}) => {
    const fetchId = ++handHeldFetchSeq;
    const { filters, pagination } = get();
    const mergedFilters = { ...filters, ...queryParams };
    const pageNumber = queryParams.pageNumber ?? pagination.pageNumber ?? 1;
    const pageSize = queryParams.pageSize ?? pagination.pageSize ?? 10;

    set({
      isLoading: true,
      error: "",
      filters: {
        circleId: mergedFilters.circleId ?? filters.circleId,
        assetCategoryId:
          mergedFilters.assetCategoryId ?? filters.assetCategoryId,
        assetTypeId: mergedFilters.assetTypeId ?? filters.assetTypeId,
        fromDate: mergedFilters.fromDate ?? filters.fromDate,
        toDate: mergedFilters.toDate ?? filters.toDate,
      },
    });

    try {
      const response = await getRFIDAssetReport({
        circleId: mergedFilters.circleId || undefined,
        assetTypeId: mergedFilters.assetTypeId || undefined,
        assetCategoryId: mergedFilters.assetCategoryId || undefined,
        fromDate: mergedFilters.fromDate || undefined,
        toDate: mergedFilters.toDate || undefined,
        pageNumber,
        pageSize,
      });

      if (fetchId !== handHeldFetchSeq) {
        return { success: false, stale: true };
      }

      const list = Array.isArray(response?.data) ? response.data : [];
      const mappedData = mapHandHeldReportData(list);
      const totalRecords = Number(response?.totalRecords) || mappedData.length;
      const resolvedPageSize = Number(response?.pageSize) || pageSize;
      const resolvedPageNumber = Number(response?.pageNumber) || pageNumber;
      const totalPages =
        Number(response?.totalPages) ||
        Math.max(1, Math.ceil(totalRecords / resolvedPageSize));

      set({
        reportRows: mappedData,
        isLoading: false,
        error: "",
        pagination: {
          pageNumber: resolvedPageNumber,
          pageSize: resolvedPageSize,
          totalRecords,
          totalPages,
        },
      });

      return { success: true, data: mappedData };
    } catch (error) {
      if (fetchId !== handHeldFetchSeq) {
        return { success: false, stale: true };
      }

      const message = getErrorMessage(
        error?.response?.data,
        "Failed to load Hand Held Reader Collection."
      );

      set({
        reportRows: [],
        isLoading: false,
        error: message,
        pagination: {
          pageNumber: 1,
          pageSize,
          totalRecords: 0,
          totalPages: 1,
        },
      });
      toast.error(message);
      return { success: false, message };
    }
  },
}));
